/**
 * PATH: apps/hr/src/pages/documents/document-detail/ActivityTimeline.tsx
 * PURPOSE: Audit trail / activity timeline for documents with real + synthetic events
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bot, UserCircle, Settings, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { hrApi } from '../../../lib/setupApi'

function formatActionTitle(action: string): string {
  const titles: Record<string, string> = {
    'create': 'Document uploaded', 'update': 'Document updated', 'approve': 'Document approved',
    'reject': 'Document rejected', 'delete': 'Document deleted', 'ocr_complete': 'OCR completed',
    'ocr_failed': 'OCR failed', 'field_edited': 'Field edited', 'tag_added': 'Tag added', 'tag_removed': 'Tag removed',
  }
  return titles[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function formatActionDescription(action: string, afterJson?: { confidence?: number }): string {
  if (action === 'ocr_complete' && afterJson?.confidence) return `Fields extracted with ${Math.round(afterJson.confidence * 100)}% confidence`
  if (action === 'approve') return 'Document was approved for use'
  if (action === 'reject') return 'Document was rejected'
  return ''
}

function formatRelativeTime(dateString: string) {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minutes ago`
  if (diffHours < 24) return `${diffHours} hours ago`
  return `${diffDays} days ago`
}

function getActorIcon(actorType: string) {
  if (actorType === 'user') return <UserCircle className="w-4 h-4" />
  if (actorType === 'ai') return <Bot className="w-4 h-4" />
  return <Settings className="w-4 h-4" />
}

function getTimelineColor(eventType: string) {
  if (eventType.includes('upload') || eventType.includes('create')) return 'bg-blue-500'
  if (eventType.includes('approve')) return 'bg-green-500'
  if (eventType.includes('reject')) return 'bg-red-500'
  if (eventType.includes('ai') || eventType.includes('processing') || eventType.includes('ocr')) return 'bg-purple-500'
  if (eventType.includes('tag')) return 'bg-amber-500'
  if (eventType.includes('field') || eventType.includes('extract')) return 'bg-cyan-500'
  if (eventType.includes('update') || eventType.includes('edit')) return 'bg-orange-500'
  return 'bg-teal-500'
}

interface ActivityTimelineProps {
  documentId: string
  uploadedAt?: string
  ocrConfidence?: number
}

export function ActivityTimeline({ documentId, uploadedAt, ocrConfidence }: ActivityTimelineProps) {
  const { data: auditData, isLoading } = useQuery({
    queryKey: ['document-audit', documentId],
    queryFn: async () => {
      try { const response = await hrApi.getDocumentAuditHistory(documentId); return response.data }
      catch { return [] }
    },
    enabled: !!documentId,
  })

  const timeline = useMemo(() => {
    if (auditData && Array.isArray(auditData) && auditData.length > 0) {
      return auditData.map((log: any) => ({
        id: log.id,
        eventType: log.action,
        title: formatActionTitle(log.action),
        description: formatActionDescription(log.action, log.after_json),
        actorType: log.action.includes('ocr') || log.action.includes('ai') ? 'ai' : 'user',
        actorName: log.action.includes('ocr') || log.action.includes('ai') ? 'Quotech AI' : 'User',
        createdAt: log.occurred_at,
      }))
    }
    const events: any[] = []
    if (uploadedAt) {
      const uploadTime = new Date(uploadedAt)
      events.push({ id: '1', eventType: 'document_uploaded', title: 'Document uploaded', description: 'Document was uploaded to the system', actorType: 'user', actorName: 'User', createdAt: uploadTime.toISOString() })
      if (ocrConfidence !== undefined && ocrConfidence > 0) {
        events.push({ id: '2', eventType: 'ai_processing', title: 'AI processing completed', description: 'OCR extraction completed', actorType: 'ai', actorName: 'Quotech AI', createdAt: new Date(uploadTime.getTime() + 5000).toISOString() })
        events.push({ id: '3', eventType: 'fields_extracted', title: 'Fields extracted', description: `Fields extracted with ${ocrConfidence}% confidence`, actorType: 'ai', actorName: 'Quotech AI', createdAt: new Date(uploadTime.getTime() + 10000).toISOString() })
      }
    }
    return events
  }, [auditData, uploadedAt, ocrConfidence])

  if (isLoading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
  if (timeline.length === 0) return <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No activity recorded yet</p>

  return (
    <div className="space-y-4">
      {timeline.map((item: any, index: number) => (
        <div key={item.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={cn('w-3 h-3 rounded-full', getTimelineColor(item.eventType))} />
            {index < timeline.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 my-1" />}
          </div>
          <div className="flex-1 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-sm text-slate-900 dark:text-white">{item.title}</p>
              <span className="text-slate-400">{getActorIcon(item.actorType)}</span>
            </div>
            {item.description && <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">{item.description}</p>}
            <p className="text-xs text-slate-500">{item.actorName} &bull; {formatRelativeTime(item.createdAt)}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
